import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import AdminNav from '../components/AdminNav';
import { getVolunteers, getDrivers, getAssignments, getLocations, getShiftTimes, getTransportStatuses } from '../services/firebase';
import './Admin.css';

const EVENT_DAYS = [
  { key: 'Thu-April-9',  label: 'Thursday, April 9' },
  { key: 'Fri-April-10', label: 'Friday, April 10' },
  { key: 'Sat-April-11', label: 'Saturday, April 11' },
  { key: 'Sun-April-12', label: 'Sunday, April 12' },
];

function AdminDashboard({ onLogout }) {
  const [volunteers, setVolunteers] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [transport, setTransport] = useState({});
  const [locations, setLocations] = useState([]);
  const [shiftTimes, setShiftTimes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [liveDay, setLiveDay] = useState(EVENT_DAYS[0].key);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    const [volData, driverData, assignData, locData, shiftData, transportData] = await Promise.all([
      getVolunteers(),
      getDrivers(),
      getAssignments(),
      getLocations(),
      getShiftTimes(),
      getTransportStatuses(),
    ]);
    setVolunteers(volData);
    setDrivers(driverData);
    setAssignments(assignData);
    setLocations(locData.filter(l => l.active).map(l => l.name).sort());
    setShiftTimes(shiftData.filter(s => s.active).sort((a, b) => a.order - b.order));
    const tmap = {};
    transportData.forEach(t => { tmap[`${t.day}_${t.location}_${t.shiftTime}`] = t; });
    setTransport(tmap);
    setLoading(false);
  };

  if (loading) {
    return (
      <div className="admin-container">
        <AdminNav onLogout={onLogout} />
        <div className="loading">Loading...</div>
      </div>
    );
  }

  // Compute shift stats from assignments collection
  const shiftsWithVolunteers = assignments.filter(a => (a.volunteers || []).some(Boolean)).length;
  const totalVolunteerSlots = assignments.reduce((sum, a) => sum + (a.volunteers || []).filter(Boolean).length, 0);
  const congregationCount = new Set([
    ...volunteers.map(v => v.congregation),
    ...drivers.map(d => d.congregation)
  ].filter(Boolean)).size;

  // Volunteer assignment stats (mirrors VolunteerLoad logic)
  const volAssignMap = {};
  assignments.forEach(a => {
    (a.volunteers || []).filter(Boolean).forEach(id => {
      if (!volAssignMap[id]) volAssignMap[id] = 0;
      volAssignMap[id]++;
    });
  });
  const volunteersWithShifts = volunteers.filter(v => (volAssignMap[v.id] || 0) > 0).length;
  const volunteersUnassigned = volunteers.length - volunteersWithShifts;
  const congregationsWithShifts = new Set(
    volunteers.filter(v => (volAssignMap[v.id] || 0) > 0 && v.congregation).map(v => v.congregation)
  ).size;

  // Activity report — local slot coverage by day (same logic as VolunteerLoad)
  const LOCAL_SLOTS_PER_SHIFT = 2;
  let dashLocalAvailable = 0;
  let dashLocalFilled = 0;
  const dashReportByDay = EVENT_DAYS.map(({ key: day, label }) => {
    const dayShifts = shiftTimes.filter(s => !s.days || s.days.includes(day));
    const availableSlots = locations.length * dayShifts.length * LOCAL_SLOTS_PER_SHIFT;
    let filledSlots = 0;
    locations.forEach(loc => {
      dayShifts.forEach(st => {
        const a = assignments.find(x => x.day === day && x.location === loc && x.shiftTime === st.name);
        const vols = a?.volunteers || [];
        if (vols[3]) filledSlots++;
        if (vols[4]) filledSlots++;
      });
    });
    dashLocalAvailable += availableSlots;
    dashLocalFilled += filledSlots;
    return { day, label, availableSlots, filledSlots };
  });

  return (
    <div className="admin-container">
      <AdminNav onLogout={onLogout} />
      <header className="admin-header">
        <h1>Admin Dashboard</h1>
      </header>

      <div className="dashboard-content">
        <div className="stats-grid">
          {/* Clickable — links to volunteer roster */}
          <Link to="/admin/roster" className="stat-card stat-card-link">
            <div className="stat-number">{volunteers.length}</div>
            <div className="stat-label">Total Volunteers</div>
            <div className="stat-hint">View roster →</div>
          </Link>

          {/* Clickable — links to drivers roster */}
          <Link to="/admin/roster?tab=drivers" className="stat-card stat-card-link">
            <div className="stat-number">{drivers.length}</div>
            <div className="stat-label">Total Drivers</div>
            <div className="stat-hint">View roster →</div>
          </Link>

          <div className="stat-card">
            <div className="stat-number">{shiftsWithVolunteers}</div>
            <div className="stat-label">Shifts Scheduled</div>
            <div className="stat-hint">shift slots with coverage</div>
          </div>

          <div className="stat-card">
            <div className="stat-number">{totalVolunteerSlots}</div>
            <div className="stat-label">Volunteer Assignments</div>
            <div className="stat-hint">total individual slot fills</div>
          </div>

          <div className="stat-card">
            <div className="stat-number">{congregationCount}</div>
            <div className="stat-label">Congregations</div>
            <div className="stat-hint">represented (all registered)</div>
          </div>

          <div className="stat-card">
            <div className="stat-number">{volunteers.length + drivers.length}</div>
            <div className="stat-label">Total Participants</div>
            <div className="stat-hint">volunteers + drivers</div>
          </div>

          <div className="stat-card info">
            <div className="stat-number">{volunteersWithShifts}</div>
            <div className="stat-label">Volunteers w/ Shifts</div>
            <div className="stat-hint">assigned at least one shift</div>
          </div>

          <div className="stat-card" style={{ borderLeft: '4px solid #dc2626' }}>
            <div className="stat-number" style={{ color: '#dc2626' }}>{volunteersUnassigned}</div>
            <div className="stat-label">Unassigned Volunteers</div>
            <div className="stat-hint">no shifts assigned</div>
          </div>

          <div className="stat-card">
            <div className="stat-number">{congregationsWithShifts}</div>
            <div className="stat-label">Congregations w/ Shifts</div>
            <div className="stat-hint">distinct congs. assigned</div>
          </div>
        </div>

        <div style={{ marginTop: '32px' }}>
          <button onClick={loadData} className="btn btn-secondary">Refresh</button>
        </div>

        {/* Activity Report */}
        {locations.length > 0 && shiftTimes.length > 0 && (
          <div className="dash-shift-summary" style={{ marginTop: '24px' }}>
            <h2 className="dash-section-title">
              Activity Report — Local Shift Coverage
              <span style={{ marginLeft: '12px', fontSize: '14px', fontWeight: 400, color: '#64748b' }}>
                {dashLocalFilled} / {dashLocalAvailable} local slots filled
                {dashLocalAvailable > 0 && (
                  <span style={{ marginLeft: 6, fontWeight: 600, color: dashLocalFilled === dashLocalAvailable ? '#15803d' : '#92400e' }}>
                    ({Math.round(dashLocalFilled / dashLocalAvailable * 100)}%)
                  </span>
                )}
              </span>
            </h2>
            <table className="vol-load-report-table">
              <thead>
                <tr>
                  <th>Day</th>
                  <th>Local Slots Available</th>
                  <th>Filled</th>
                  <th>Unfilled</th>
                  <th>Coverage</th>
                </tr>
              </thead>
              <tbody>
                {dashReportByDay.map(r => {
                  const pct = r.availableSlots > 0 ? Math.round(r.filledSlots / r.availableSlots * 100) : 0;
                  return (
                    <tr key={r.day}>
                      <td>{r.label}</td>
                      <td>{r.availableSlots}</td>
                      <td style={{ color: '#15803d', fontWeight: 600 }}>{r.filledSlots}</td>
                      <td style={{ color: r.availableSlots - r.filledSlots > 0 ? '#b91c1c' : '#64748b', fontWeight: 600 }}>
                        {r.availableSlots - r.filledSlots}
                      </td>
                      <td>
                        <div className="vol-load-bar-wrap">
                          <div className="vol-load-bar-fill" style={{ width: `${pct}%` }} />
                          <span className="vol-load-bar-label">{pct}%</span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                <tr className="vol-load-report-total">
                  <td><strong>Total</strong></td>
                  <td><strong>{dashLocalAvailable}</strong></td>
                  <td style={{ color: '#15803d', fontWeight: 700 }}>{dashLocalFilled}</td>
                  <td style={{ color: dashLocalAvailable - dashLocalFilled > 0 ? '#b91c1c' : '#64748b', fontWeight: 700 }}>
                    {dashLocalAvailable - dashLocalFilled}
                  </td>
                  <td>
                    <div className="vol-load-bar-wrap">
                      <div className="vol-load-bar-fill" style={{ width: `${dashLocalAvailable > 0 ? Math.round(dashLocalFilled / dashLocalAvailable * 100) : 0}%` }} />
                      <span className="vol-load-bar-label">
                        {dashLocalAvailable > 0 ? Math.round(dashLocalFilled / dashLocalAvailable * 100) : 0}%
                      </span>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {/* Live Shift Status */}
        {locations.length > 0 && shiftTimes.length > 0 && (
          <div className="dash-shift-summary" style={{ marginTop: '32px' }}>
            <h2 className="dash-section-title">Live Shift Status</h2>
            <div className="dash-day-tabs">
              {EVENT_DAYS.map(day => (
                <button
                  key={day.key}
                  className={`day-tab${liveDay === day.key ? ' active' : ''}`}
                  style={{ marginRight: '6px', marginBottom: '12px' }}
                  onClick={() => setLiveDay(day.key)}
                >
                  {day.label}
                </button>
              ))}
            </div>
            <div className="dash-live-grid">
              {(() => {
                const cells = [];
                shiftTimes.forEach(st => {
                  locations.forEach(loc => {
                    const key = `${liveDay}_${loc}_${st.name}`;
                    const localCount = (assignments.find(a => a.day === liveDay && a.location === loc && a.shiftTime === st.name)?.volunteers || []).filter(Boolean).length;
                    if (localCount === 0) return;
                    const td = transport[key] || {};
                    const smpwCount = Math.max(0, Number(td.smpwCount) || 0);
                    const total = localCount + smpwCount;
                    let status = 'notstarted';
                    if (td.pickupDone) status = 'complete';
                    else if (td.dropoffDone) status = 'inprogress';
                    cells.push({ shiftTime: st.name, loc, total, smpwCount, localCount, status });
                  });
                });
                if (cells.length === 0) return (
                  <p style={{ color: '#94a3b8', fontSize: '13px' }}>No assignments for this day yet.</p>
                );
                return cells.map(cell => (
                  <div key={`${cell.loc}_${cell.shiftTime}`} className={`dash-live-cell dash-live-${cell.status}`}>
                    <div className="dash-live-shift">{cell.shiftTime}</div>
                    <div className="dash-live-loc">{cell.loc}</div>
                    <div className="dash-live-count">
                      {cell.total} vol
                      {cell.smpwCount > 0 && <span style={{ fontSize: '10px', opacity: 0.75 }}> ({cell.localCount}+{cell.smpwCount})</span>}
                    </div>
                    <div className="dash-live-status">
                      {cell.status === 'complete'    && '✅ Picked up'}
                      {cell.status === 'inprogress'  && '🟡 Dropped off'}
                      {cell.status === 'notstarted'  && '⬜ Not started'}
                    </div>
                  </div>
                ));
              })()}
            </div>
          </div>
        )}

        {/* Shift Summary */}
        {locations.length > 0 && shiftTimes.length > 0 && (
          <div className="dash-shift-summary">
            <h2 className="dash-section-title">Shift Coverage by Day</h2>
            <div className="dash-day-tabs">
              {EVENT_DAYS.map(day => {
                // Build assignment map for this day: location_shiftTime -> filled count
                const dayAssignments = assignments.filter(a => a.day === day.key);
                const assignMap = {};
                dayAssignments.forEach(a => {
                  assignMap[`${a.location}__${a.shiftTime}`] = (a.volunteers || []).filter(Boolean).length;
                });

                const totalSlots = locations.length * shiftTimes.length;
                const filledSlots = Object.values(assignMap).reduce((s, n) => s + (n > 0 ? 1 : 0), 0);

                return (
                  <div key={day.key} className="dash-day-block">
                    <div className="dash-day-header">
                      <span className="dash-day-label">{day.label}</span>
                      <span className="dash-day-tally">{filledSlots}/{totalSlots} slots covered</span>
                    </div>
                    <div className="dash-shift-grid" style={{ gridTemplateColumns: `160px repeat(${shiftTimes.length}, 1fr)` }}>
                      {/* Header row */}
                      <div className="dash-grid-header dash-grid-loc-col"></div>
                      {shiftTimes.map(st => (
                        <div key={st.id} className="dash-grid-header dash-grid-time-col">{st.name}</div>
                      ))}
                      {/* Location rows */}
                      {locations.map(loc => (
                        <React.Fragment key={loc}>
                          <div className="dash-grid-loc-col dash-grid-loc-name">{loc}</div>
                          {shiftTimes.map(st => {
                            const count = assignMap[`${loc}__${st.name}`] || 0;
                            const cls = count === 0 ? 'dash-cell-empty' : 'dash-cell-filled';
                            return (
                              <div key={st.id} className={`dash-shift-cell ${cls}`}>
                                {count > 0 ? count : '–'}
                              </div>
                            );
                          })}
                        </React.Fragment>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminDashboard;
